import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {withStyles} from "@material-ui/core/styles";
import EditableInput from "../Controls/EditableInput";
import {green, orange, red, yellow} from "@material-ui/core/colors";
import SignalCellularOffIcon from "@material-ui/icons/SignalCellularOff";
import SignalCellular0BarIcon from "@material-ui/icons/SignalCellular0Bar";
import SignalCellular1BarIcon from "@material-ui/icons/SignalCellular1Bar";
import SignalCellular2BarIcon from "@material-ui/icons/SignalCellular2Bar";
import SignalCellular3BarIcon from "@material-ui/icons/SignalCellular3Bar";
import SignalCellular4BarIcon from "@material-ui/icons/SignalCellular4Bar";
import MicOffIcon from "@material-ui/icons/MicOff";
import Logger from "../../Logger";
import {injectIntl} from "react-intl";
import * as roomActions from "../../actions/roomActions";
import {connect} from "react-redux";

const logger = new Logger("VideoView");
const styles = (theme) => ({
  root: {
    position: "relative",
    flex: "100 100 auto",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  video: {
    flex: "100 100 auto",
    height: "100%",
    width: "100%",
    objectFit: "cover",
    userSelect: "none",
    transitionProperty: "opacity",
    transitionDuration: ".15s",
    borderRadius: 16,
    backgroundColor: "var(--peer-video-bg-color)",
    "&.isMirrored": {
      transform: "scaleX(-1)",
    },
    "&.hidden": {
      opacity: 0,
      transitionDuration: "0s",
    },
    "&.loading": {
      filter: "blur(5px)",
    },
    "&.contain": {
      objectFit: "contain",
      backgroundColor: "rgba(0, 0, 0, 1)",
    },
  },
  info: {
    width: "100%",
    height: "100%",
    padding: theme.spacing(1),
    position: "absolute",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  media: {
    display: "flex",
    transitionProperty: "opacity",
    transitionDuration: ".15s",
    "&.hidden": {
      opacity: 0,
      transitionDuration: "0s",
    },
  },
  box: {
    padding: theme.spacing(0.5),
    borderRadius: 2,
    textAlign: "center",
    backgroundColor: "rgba(0, 0, 0, 0.25)",
    "&.info": {
      backgroundColor: "transparent",
      position: "absolute",
      top: 0,
      left:0,
      bottom:0,
      right:0,
      height: "100%",
      width: "100%",
      display:"flex",
      justifyContent:"space-between",
      flexDirection:"column",
      alignItems: "center",
    },
    "& p": {
      userSelect: "none",
      margin: 0,
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "0.8em",
    },
    "&.left": {
      backgroundColor: "rgba(0, 0, 0, 0.25)",
    },
    "&.right": {
      marginLeft: "auto",
      width: 30,
    },
    "&.roleBadge": {
      maxWidth: "100%",
      width: "fit-content",
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      userSelect: "none",
      fontSize: 14,
      fontWeight: 400,
      color: "rgba(255, 255, 255, 0.85)",
    },
    "&.hidden": {
      opacity: 0,
      transitionDuration: "0s",
    },
  },
  peer: {
    display: "flex",
  },
  displayNameEdit: {
    fontSize: 14,
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.85)",
    border: "none",
    borderBottom: "1px solid #0A8AFF",
    paddingLeft: "5px",
    paddingRight: "5px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    maxWidth: "100%"
  },
  displayNameStatic: {
    userSelect: "none",
    cursor: "text",
    fontSize: 14,
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.85)",
    maxWidth: "100%",
    paddingLeft: "5px",
    paddingRight: "5px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(10,138,255,0.1)",
    },
  },
});

class VideoView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      videoWidth: null,
      videoHeight: null,
    };

    // Latest received video track.
    // @type {MediaStreamTrack}
    this._videoTrack = null;

    // Periodic timer for showing video resolution.
    this._videoResolutionTimer = null;
  }

  render() {
    const {
      roomClient,
      isMe,
      isMirrored,
      pictureInPicture,
      isScreen,
      displayName,
      showPeerInfo,
      videoContain,
      advancedMode,
      videoVisible,
      videoMultiLayer,
      audioScore,
      videoScore,
      // consumerSpatialLayers,
      // consumerTemporalLayers,
      consumerCurrentSpatialLayer,
      consumerCurrentTemporalLayer,
      consumerPreferredSpatialLayer,
      consumerPreferredTemporalLayer,
      audioCodec,
      videoCodec,
      onChangeDisplayName,
      micOff,
      children,
      classes,
      peer,
      toggleControlBarVisibility,
      theme,
    } = this.props;

    const { videoWidth, videoHeight } = this.state;

    let quality = (
      <SignalCellularOffIcon style={{ fontSize: 15, color: red[500] }} />
    );

    if (videoScore || audioScore) {
      const score = videoScore ? videoScore : audioScore;

      switch (score.producerScore) {
        case 0:
        case 1: {
          quality = (
            <SignalCellular0BarIcon style={{ fontSize: 15, color: red[500] }} />
          );

          break;
        }

        case 2:
        case 3: {
          quality = (
            <SignalCellular1BarIcon style={{ fontSize: 15, color: red[500] }} />
          );

          break;
        }

        case 4:
        case 5:
        case 6: {
          quality = (
            <SignalCellular2BarIcon
              style={{ fontSize: 15, color: orange[500] }}
            />
          );

          break;
        }

        case 7:
        case 8: {
          quality = (
            <SignalCellular3BarIcon
              style={{ fontSize: 15, color: yellow[500] }}
            />
          );

          break;
        }

        case 9:
        case 10: {
          quality = (
            <SignalCellular4BarIcon
              style={{ fontSize: 15, color: green[500] }}
            />
          );

          break;
        }

        default: {
          break;
        }
      }
    }

    const roleBadge = this.findRoleBadge(peer);

    const backgroundStyle = () => {
      if (peer && peer.picture) {
        return {
          backgroundImage: `url(${peer.picture})`,
          backgroundColor: "white",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          borderRadius: 16
        };
      } else {
        return null;
      }
    };

    return (
      <div className={classes.root} style={backgroundStyle()}>
        {  !this.props.pictureinPicture&&(<div className={classes.info}>
          <div className={classes.media} id={"media"}>
            {/*            <div className={classnames(classes.box, 'left')}>
              {audioCodec && <p>{audioCodec}</p>}

              {videoCodec &&
              <p>
                {videoCodec}
              </p>
              }

              {videoMultiLayer &&
              <p>
                {`current spatial-temporal layers: ${consumerCurrentSpatialLayer} ${consumerCurrentTemporalLayer}`}
                <br/>
                {`preferred spatial-temporal layers: ${consumerPreferredSpatialLayer} ${consumerPreferredTemporalLayer}`}
              </p>
              }

              {(videoVisible && videoWidth !== null) &&
              <p>{videoWidth}x{videoHeight}</p>}

            </div>*/}
            {(audioScore || videoScore) && (
              <div className={classnames(classes.box, "right")}>
                {micOff ? <MicOffIcon id={"micoff"} /> : null}
                {quality}
              </div>
            )}
          </div>

          {showPeerInfo && (
            <div className={classes.peer}>
              <div className={classnames(classes.box, "info")}>
              {roleBadge && (
                   <div className={classnames(classes.box, "roleBadge")}>
                   <span>{roleBadge}</span>
                  </div>
              )}
              {!roleBadge &&<div></div>}
                {isMe ? (
                  <EditableInput
                    theme={theme}
                    value={displayName}
                    propName="newDisplayName"
                    className={classes.displayNameEdit}
                    classLoading="loading"
                    classInvalid="invalid"
                    shouldBlockWhileLoading
                    editProps={{
                      maxLength: 25,
                      autoCorrect: "off",
                      spellCheck: false,
                    }}
                    onChange={({ newDisplayName }) =>
                      onChangeDisplayName(newDisplayName)
                    }
                  />
                ) : (
                  <span className={classes.displayNameStatic}>
                    {displayName}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>)}

        <video
          id={displayName + " videoElement"}
          ref="videoElement"
          className={classnames(classes.video, {
            hidden: !videoVisible,
            isMe: isMe && !isScreen,
            isMirrored: isMirrored,
            contain: videoContain,
          })}
          autoPlay
          playsInline
          muted
          controls={false}
        />

        {children}
      </div>
    );
  }

  componentDidMount() {
    const { videoTrack, roomClient } = this.props;

    this._setTracks(videoTrack);
  }

  componentWillUnmount() {
    const { toggleControlBarVisibility } = this.props;
    const { bottomBarVisibilityInterval } = this.props;
    clearInterval(this._videoResolutionTimer);

    const { videoElement } = this.refs;

    if (videoElement) {
      videoElement.oncanplay = null;
      videoElement.onplay = null;
      videoElement.onpause = null;
    }
    toggleControlBarVisibility(true);
    for (let i = 0; i <= bottomBarVisibilityInterval; i++) clearTimeout(i);
  }

  // eslint-disable-next-line camelcase
  componentWillUpdate() {
    const { videoTrack } = this.props;

    this._setTracks(videoTrack);
  }

  _setTracks(videoTrack) {
    const { roomClient } = this.props;

    if (this._videoTrack === videoTrack) return;

    this._videoTrack = videoTrack;

    clearInterval(this._videoResolutionTimer);
    this._hideVideoResolution();

    const { videoElement } = this.refs;

    if (videoTrack) {
      const stream = new MediaStream();

      stream.addTrack(videoTrack);
      //roomClient.appendRecording(stream);

      videoElement.srcObject = stream;

      videoElement.oncanplay = () => this.setState({ videoCanPlay: true });

      videoElement
        .play()
        .catch((error) =>
          logger.warn('videoElement.play() [error:"%o]', error)
        );

      this._showVideoResolution();
    } else {
      videoElement.srcObject = null;
    }
  }

  _showVideoResolution() {
    this._videoResolutionTimer = setInterval(() => {
      const { videoWidth, videoHeight } = this.state;
      const { videoElement } = this.refs;

      // Don't re-render if nothing changed.
      if (
        videoElement.videoWidth === videoWidth &&
        videoElement.videoHeight === videoHeight
      )
        return;

      this.setState({
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
      });
    }, 1000);
  }

  _hideVideoResolution() {
    this.setState({ videoWidth: null, videoHeight: null });
  }

  findRoleBadge(peer) {
    const { intl } = this.props;

    if (!peer) return;

    if (peer.roles.includes("admin"))
      return intl.formatMessage({
        id: "label.admin",
        defaultMessage: "Admin",
      });
    else if (peer.roles.includes("moderator"))
      return intl.formatMessage({
        id: "label.moderator",
        defaultMessage: "Moderator",
      });
    else if (peer.roles.includes("normal") && peer.roles.length === 1)
      return intl.formatMessage({
        id: "label.guest",
        defaultMessage: "Guest",
      });
    else if (peer.roles.includes("interpreter"))
      return intl.formatMessage({
        id: "label.interpreter",
        defaultMessage: "Interpreter",
      });
    return null;
  }
}

VideoView.propTypes = {
  isMe: PropTypes.bool,
  isScreen: PropTypes.bool,
  displayName: PropTypes.string,
  showPeerInfo: PropTypes.bool,
  videoContain: PropTypes.bool,
  advancedMode: PropTypes.bool,
  micOff: PropTypes.bool,
  videoTrack: PropTypes.any,
  videoVisible: PropTypes.bool.isRequired,
  consumerSpatialLayers: PropTypes.number,
  consumerTemporalLayers: PropTypes.number,
  consumerCurrentSpatialLayer: PropTypes.number,
  consumerCurrentTemporalLayer: PropTypes.number,
  consumerPreferredSpatialLayer: PropTypes.number,
  consumerPreferredTemporalLayer: PropTypes.number,
  videoMultiLayer: PropTypes.bool,
  audioScore: PropTypes.any,
  videoScore: PropTypes.any,
  audioCodec: PropTypes.string,
  videoCodec: PropTypes.string,
  onChangeDisplayName: PropTypes.func,
  children: PropTypes.object,
  classes: PropTypes.object.isRequired,
  toggleControlBarVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    theme: state.theme.defaultTheme,
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggleControlBarVisibility: (visibility) => {
    dispatch(roomActions.setControlBarVisibility(visibility));
  },
});
export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual: (next, prev) => {
    return (
      prev.consumers[prev.room.fullScreenConsumer] ===
        next.consumers[next.room.fullScreenConsumer] &&
      prev.room.toolbarsVisible === next.room.toolbarsVisible &&
      prev.settings.permanentTopBar === next.settings.permanentTopBar &&
      prev.theme === next.theme
    );
  },
})(withStyles(styles)(injectIntl(VideoView)));
